import Panel from "ui/Panel";
import React, { useState } from "react";

import { useWorkbookDetailsQuery } from "../Workbook/gql/Query.generated";
import { WorkbookType, WorkbookVariantType } from "../Workbook";

import Grid, { NumberCell } from "ui/Grid";
import WorkbookDiff from "features/Workbooks/WorkbookDiff";
import { DiffModeOption } from "types/WorkbookDiff";
import { Link, useParams } from "react-router-dom";
import { useRoutes } from "utils/routes";
import Loading from "ui/Loading";
import PageContent from "ui/PageContent";
import { WorkbookFeatureNav, RuntimeMsCell } from "../Workbook";
import FillMissingParameterValues, {
  missingValueAliases,
} from "../FillMissingParameterValues";
import RunExplain from "../RunExplain";
import WorkbookVariantSidebar from "../WorkbookVariantSidebar";
import ExplainCompareLinkBar from "../ExplainCompareLinkBar";
import { mru } from "utils/array";
import ChooseParameters from "../ChooseParameters";
import ExplainFingerprint from "features/Explain/ExplainFingerprint";

// This is not really tab, but for which page to show
type VariantTabType = "run";

const WorkbookVariant = ({ tab }: { tab?: VariantTabType }) => {
  const { databaseId, workbookId, variantId } = useParams();
  const { databaseWorkbookVariantResult, databaseWorkbookVariantRun } =
    useRoutes();
  const [compareCandidates, setCompareCandidates] = useState<string[]>([]);
  function handleToggleCompareCandidate(
    e: React.ChangeEvent<HTMLInputElement>,
  ) {
    const { checked } = e.currentTarget;
    const candidateId = e.currentTarget.dataset.compareId;
    const newCandidates = checked
      ? mru(candidateId, compareCandidates, 2)
      : compareCandidates.filter((id) => id != candidateId);

    setCompareCandidates(newCandidates);
  }

  const { loading, error, data } = useWorkbookDetailsQuery({
    variables: { workbookId, databaseId },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }
  const workbook = data.getWorkbookDetails;

  // During the initial workbook creation flow:
  // If the selection is not done yet, show the selection page
  if (!workbook.parameterSetsSelected) {
    return <ChooseParameters workbook={workbook} />;
  }

  const variant =
    workbook.baselineQuery.id === variantId
      ? workbook.baselineQuery
      : workbook.variants.find((val) => val.id === variantId);

  const noParamSets = workbook.parameterSets.length === 0;

  const featureNav = (
    <WorkbookFeatureNav workbookId={workbookId} databaseId={databaseId} />
  );

  // When there is zero result uploaded, or with "run" path, show Run EXPLAIN
  if (variant?.workbookResults.length === 0 || tab === "run") {
    // Check if this variant's aliases contain missing value aliases
    // (can't run EXPLAIN until missing value aliases are all set)
    const missingAliases = Array.from(
      new Set(
        workbook.parameterSets.flatMap((ps) =>
          missingValueAliases(workbook, ps.id).map((v) => `$${v}`),
        ),
      ),
    );
    if (
      missingAliases.some((alias) =>
        Object.values(variant.paramRefAliasMap).includes(alias),
      )
    ) {
      // EXPLAIN can't run until every parameter set has a value for each of this
      // variant's parameters, so collect the missing values first
      return (
        <FillMissingParameterValues workbook={workbook} variant={variant} />
      );
    } else if (variant) {
      return <RunExplain workbook={workbook} variant={variant} />;
    }
  }

  let fastestRuntime = Infinity;
  const variantData = workbook.parameterSets
    .map((ps) => {
      const result = variant?.workbookResults.find(
        (er) => er.parameterSetId === ps.id,
      );
      // This all takes place synchronously before render: eslint is flagging this spuriously
      // eslint-disable-next-line react-hooks/immutability
      fastestRuntime = Math.min(fastestRuntime, result?.runtimeMs ?? Infinity);
      return {
        resultId: result?.id,
        setId: ps.id,
        paramSetName: ps.name,
        planFingerprint: result?.planFingerprint,
        totalCost: result?.totalCost,
        runtimeMs: result?.runtimeMs,
        totalBlkReadTime: result?.totalBlkReadTime,
        errorMessage: result?.errorMessage,
        resultSource: result?.resultSource,
        resultStatus: result?.status,
      };
    })
    .sort((a, b) => Number(a.setId) - Number(b.setId));

  if (noParamSets) {
    const result = variant?.workbookResults[0];
    variantData.push({
      resultId: result?.id,
      setId: null,
      paramSetName: null,
      planFingerprint: result?.planFingerprint,
      totalCost: result?.totalCost,
      runtimeMs: result?.runtimeMs,
      totalBlkReadTime: result?.totalBlkReadTime,
      errorMessage: result?.errorMessage,
      resultSource: result?.resultSource,
      resultStatus: result?.status,
    });
  }

  return (
    <PageContent
      windowTitle={`EXPLAIN Workbook: ${workbook.name}`}
      title={workbook.name}
      pageCategory="workbooks"
      pageName="show-variant"
      layout="sidebar"
      featureNav={featureNav}
    >
      {variant ? (
        <>
          <VariantPanel workbook={workbook} variant={variant} />
          <Panel title="Query Plans">
            <Grid
              data={variantData}
              columns={[
                {
                  header: "",
                  disableSort: true,
                  field: "resultId",
                  width: "34px",
                  renderer: function CompareSelectCell({ fieldData, rowData }) {
                    const checked = compareCandidates.includes(fieldData);
                    return (
                      <input
                        type="checkbox"
                        data-compare-id={fieldData}
                        className="cursor-pointer !mt-0.5"
                        checked={checked}
                        onChange={handleToggleCompareCandidate}
                        disabled={!rowData.planFingerprint}
                      />
                    );
                  },
                },
                {
                  field: "planFingerprint",
                  header: "Plan",
                  width: "16%",
                  renderer: function PlanFingerprintCell({
                    fieldData,
                    rowData,
                  }) {
                    if (rowData.resultId == null || fieldData == null) {
                      return (
                        <Link
                          to={databaseWorkbookVariantRun(
                            databaseId,
                            workbook.id,
                            variant.id,
                          )}
                        >
                          Run EXPLAIN
                        </Link>
                      );
                    }
                    return (
                      <Link
                        to={databaseWorkbookVariantResult(
                          databaseId,
                          workbook.id,
                          variant.id,
                          rowData.resultId,
                        )}
                      >
                        <ExplainFingerprint fingerprint={fieldData} />
                      </Link>
                    );
                  },
                },
                {
                  field: "setId",
                  header: "Variant",
                  width: "1fr",
                  renderer: () => variant.name,
                },
                {
                  field: "paramSetName",
                  header: "Parameter Set",
                  width: "18%",
                },
                {
                  field: "totalCost",
                  header: "Est. Cost",
                  width: "18%",
                  renderer: NumberCell,
                  style: "number",
                  nullValue: "-",
                },
                {
                  field: "runtimeMs",
                  header: "Runtime",
                  width: "18%",
                  renderer: ({ fieldData, rowData }) => {
                    return (
                      <RuntimeMsCell
                        fieldData={fieldData}
                        rowData={rowData}
                        fastestRuntime={fastestRuntime}
                      />
                    );
                  },
                  style: "number",
                },
              ]}
            />
          </Panel>
          <ExplainCompareLinkBar
            databaseId={databaseId}
            workbookId={workbook.id}
            compareCandidates={compareCandidates}
          />
        </>
      ) : (
        <Loading error />
      )}
      {/* sidebar */}
      <div className="2xl:w-[400px] w-[320px]">
        <WorkbookVariantSidebar
          workbook={workbook}
          variant={variant}
          permittedToCreateWorkbooks={
            workbook.database.permittedToCreateWorkbooks
          }
        />
      </div>
    </PageContent>
  );
};

export const VariantPanel = ({
  workbook,
  variant,
}: {
  workbook: WorkbookType;
  variant: WorkbookVariantType;
}) => {
  const [selectedParameterSetId, setSelectedParameterSetId] = useState("");
  const handleParameterSetSelected = (selected: string) => {
    setSelectedParameterSetId(selected);
  };
  const plannerSettingsCommand =
    Object.entries(variant.plannerSettings)
      .map(([key, value]) => {
        return `SET ${key} = ${value};`;
      })
      .join("\n") +
    (Object.entries(variant.plannerSettings).length > 0 ? "\n\n" : "");
  const queryText = selectedParameterSetId
    ? variant.queryTextWithParameters.find(
        (val) => val.parameterSetId === selectedParameterSetId,
      ).queryWithParameters
    : variant.queryTextWithAlias;
  const modified = plannerSettingsCommand + queryText;

  const options: DiffModeOption[] = [
    { value: "read", label: "SQL Query" },
    {
      value: "diff-unified",
      label: "Unified Diff",
    },
    {
      value: "diff-split",
      label: "Split Diff",
    },
  ];

  const original = workbook.baselineQuery.queryTextWithAlias;
  const canExpand = original.split("\n").length > 15;
  const isBaseline = workbook.baselineQuery.id === variant.id;

  return (
    <div className="rounded-md bg-[#f7fafc] border border-[#E8E8EE] p-4 mb-4 grid gap-2 text-[#606060] grid-cols-1">
      <div className="flex">
        <div className="flex-grow text-[18px] leading-6">{variant.name}</div>
        <div>
          <select
            value={selectedParameterSetId}
            onChange={(e) => handleParameterSetSelected(e.target.value)}
            className="bg-inherit"
          >
            <option value="">With parameter names</option>
            {workbook.parameterSets.map((set) => {
              return (
                <option key={set.id} value={set.id}>
                  With {set.name}
                </option>
              );
            })}
          </select>
        </div>
      </div>
      <WorkbookDiff
        original={original}
        modified={modified}
        options={options}
        isBaseline={isBaseline}
        canExpand={canExpand}
        displayCTA={!isBaseline && original === modified}
        diffViewClassName={canExpand ? "overflow-y-auto" : ""}
      />
    </div>
  );
};

export default WorkbookVariant;
